import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import { SupplierPaymentService } from '../../services/supplierPayment.service';
import { SupplierBillService } from '../../services/supplierBill.service';
import { SupplierService } from '../../services/supplier.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { SupplierBill } from '../../interfaces/procurement/supplierBill.interface';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';

const today = () => new Date().toISOString().slice(0, 10);

export const SupplierPaymentCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const lockedBillId = searchParams.get('supplierBillId');

  const institutionId = AuthService.getSelectedInstitution()!;

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [bills, setBills] = useState<SupplierBill[]>([]);
  const [selectedBill, setSelectedBill] = useState<SupplierBill | null>(null);
  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);

  const [paymentDate, setPaymentDate] = useState(today());
  const [amount, setAmount] = useState<number | ''>('');
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [reference, setReference] = useState('');

  const [loadingBill, setLoadingBill] = useState(!!lockedBillId);
  const [loadingSuppliers, setLoadingSuppliers] = useState(!lockedBillId);
  const [saving, setSaving] = useState(false);

  // Outstanding amount
  const outstanding = selectedBill
    ? selectedBill.totalAmount - selectedBill.paidAmount
    : 0;

  // Load asset accounts
  useEffect(() => {
    AccountService.getAccountsByType('ASSET')
      .then((accounts: Account[]) => setAssetAccounts(accounts))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load accounts'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load locked bill or suppliers
  useEffect(() => {
    if (lockedBillId) {
      SupplierBillService.getById(lockedBillId)
        .then(bill => {
          setSelectedBill(bill);
          setAmount(bill.totalAmount - bill.paidAmount);
        })
        .catch(e => toast.showApiError(e, 'Failed to load supplier bill'))
        .finally(() => setLoadingBill(false));
    } else {
      SupplierService.listByInstitution(0, 500)
        .then(r => setSuppliers(r.content))
        .catch(e => toast.showApiError(e, 'Failed to load suppliers'))
        .finally(() => setLoadingSuppliers(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSupplierChange = async (supplierId: string) => {
    setSelectedSupplierId(supplierId);
    setSelectedBill(null);
    setBills([]);
    setAmount('');
    try {
      const res = await SupplierBillService.listBySupplier(supplierId, 0, 500);
      setBills(res.content);
    } catch (e) {
      toast.showApiError(e, 'Failed to load bills for supplier');
    }
  };

  const handleBillChange = (billId: string) => {
    const bill = bills.find(b => b.id === billId) ?? null;
    setSelectedBill(bill);
    if (bill) {
      setAmount(bill.totalAmount - bill.paidAmount);
    } else {
      setAmount('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBill || !paymentAccountId || amount === '') return;
    setSaving(true);
    try {
      const payment = await SupplierPaymentService.create({
        supplierBillId: selectedBill.id,
        paymentDate,
        amount: Number(amount),
        paymentAccountId,
        paymentMethod: paymentMethod || undefined,
        reference: reference || undefined,
        institutionId,
      });
      toast.showSuccess('Payment recorded successfully');
      navigate(`/procurement/supplier-payments/${payment.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  if (loadingBill || loadingSuppliers) {
    return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Record Supplier Payment</Typography>
      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Bill picker — locked or interactive */}
          {lockedBillId ? (
            <Box>
              <Typography variant="body2" color="text.secondary">Supplier Bill (locked)</Typography>
              <Typography>{selectedBill?.billNumber ?? lockedBillId}</Typography>
            </Box>
          ) : (
            <>
              <TextField
                select
                label="Supplier"
                value={selectedSupplierId}
                onChange={e => handleSupplierChange(e.target.value)}
                fullWidth
                required
              >
                {suppliers.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>

              {selectedSupplierId && (
                <TextField
                  select
                  label="Supplier Bill"
                  value={selectedBill?.id ?? ''}
                  onChange={e => handleBillChange(e.target.value)}
                  fullWidth
                  required
                >
                  {bills.map(b => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.billNumber} — {b.status} (Total: {b.totalAmount}, Paid: {b.paidAmount})
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </>
          )}

          {/* Bill summary */}
          {selectedBill && (
            <Box sx={{ backgroundColor: 'action.hover', p: 2, borderRadius: 1 }}>
              <Typography variant="body2">Total: {selectedBill.totalAmount}</Typography>
              <Typography variant="body2">Paid: {selectedBill.paidAmount}</Typography>
              <Typography variant="body2" fontWeight="bold">Outstanding: {outstanding}</Typography>
            </Box>
          )}

          <TextField
            label="Payment Date"
            type="date"
            value={paymentDate}
            onChange={e => setPaymentDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            inputProps={{ min: 0, step: 0.01 }}
            fullWidth
            required
          />

          <AccountAutocomplete
            label="Payment Account"
            accounts={assetAccounts}
            value={paymentAccountId}
            onChange={setPaymentAccountId}
            required
          />

          <TextField
            label="Payment Method (optional)"
            value={paymentMethod}
            onChange={e => setPaymentMethod(e.target.value)}
            fullWidth
          />

          <TextField
            label="Reference (optional)"
            value={reference}
            onChange={e => setReference(e.target.value)}
            fullWidth
          />

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !selectedBill || !paymentAccountId || amount === ''}
            >
              {saving ? 'Saving…' : 'Record Payment'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SupplierPaymentCreate;
